/**
 * Y-axis domains for the trend charts, so the threshold line is always on screen.
 *
 * ── The bug this exists to stop ──────────────────────────────────────────────────────────
 * Recharts computes a `<YAxis>`'s default domain from the DATA SERIES ONLY. A `<ReferenceLine>`
 * contributes nothing to it. So a limit sitting well under its threshold — 29.87% against a
 * 40.00% threshold — scaled the axis to about 32 and drew the dashed threshold line above the
 * top of the plot, where it was clipped away entirely. The line appeared and disappeared
 * depending on whether the limit happened to be near breach, which is exactly backwards: the
 * further from the threshold, the more likely it was to vanish.
 *
 * Both detail sheets (Monitor and Capacity) left `<YAxis>` bare and had this. The sparklines
 * in both tables already passed an explicit domain including the threshold, which is where
 * this rule came from — it is now one function rather than the same expression written out
 * three times and forgotten in two more places.
 */

/** Room above the taller of (data, threshold), so the line is not welded to the frame. */
const HEADROOM = 1.1;

/**
 * `[min, max]` for a percentage trend chart that must show `threshold`.
 *
 * Pass the threshold as it is stored — percent, not a fraction — matching `dollarPct` on the
 * trend points.
 *
 * The upper bound is a FUNCTION, which Recharts calls with the data's own maximum. That
 * matters: the caller does not have to reach into the series to compute it, and it stays
 * correct if the series is filtered or reshaped later.
 *
 * A non-finite threshold yields the data-driven bound unchanged. Capacity's
 * `bindingBreachPct` returns Infinity for a row with no percentage threshold and no capital
 * cap, and `Math.max(dataMax, Infinity) * 1.1` would collapse the whole plot to the baseline.
 */
export function trendDomain(
  threshold: number | null | undefined,
): [number, (dataMax: number) => number] {
  const t = typeof threshold === "number" && Number.isFinite(threshold) ? threshold : 0;
  return [0, (dataMax: number) => Math.max(dataMax, t) * HEADROOM];
}
